import { Button } from "@/components/ui/button";
import { PlaceholderStatus } from "@/hooks/use-user-switch-animation-init";
import { cn } from "@/lib/utils";
import { AlertCircle, CheckCircle, RotateCcw, SkipForward } from "lucide-react";
import { FC } from "react";

interface UserSwitchPlaceholderProps {
  status: PlaceholderStatus;
  isVisible: boolean;
  onReturn?: () => void;
}

const UserSwitchPlaceholder: FC<UserSwitchPlaceholderProps> = ({
  status,
  isVisible,
  onReturn,
}) => {
  if (!isVisible || !status) return null;

  const getStatusConfig = () => {
    switch (status) {
      case "processed":
        return {
          icon: CheckCircle,
          text: "Оброблено",
          className: "text-green-600 bg-green-100 border-green-200",
          showReturnButton: false,
        };
      case "returned":
        return {
          icon: RotateCcw,
          text: "Повернуто",
          className: "text-blue-600 bg-blue-100 border-blue-200",
          showReturnButton: false,
        };
      case "error":
        return {
          icon: AlertCircle,
          text: "Помилка",
          className: "text-red-600 bg-red-100 border-red-200",
          showReturnButton: true,
        };
      case "skipped":
        return {
          icon: SkipForward,
          text: "Користувача пропущено",
          className: "text-orange-600 bg-orange-100 border-orange-200",
          showReturnButton: false,
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center transition-all duration-500 ease-out",
        isVisible
          ? "bg-black/20 backdrop-blur-sm opacity-100"
          : "bg-black/0 backdrop-blur-none opacity-0 pointer-events-none",
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center gap-4 px-6 py-4 rounded-lg border-2 shadow-lg backdrop-blur-md bg-white/90 transition-all duration-500 ease-out transform",
          config.className,
          isVisible
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-95 opacity-0 translate-y-4",
        )}
      >
        <div className="flex items-center gap-3 animate-in slide-in-from-top-2 duration-300">
          <IconComponent className="h-6 w-6 animate-in zoom-in-50 duration-300 delay-100" />
          <span className="text-lg font-semibold animate-in slide-in-from-left-2 duration-300 delay-200">
            {config.text}
          </span>
        </div>

        {config.showReturnButton && onReturn && (
          <Button
            variant="outline"
            size="sm"
            onClick={onReturn}
            className="flex items-center gap-2 bg-white/80 hover:bg-white backdrop-blur-sm transition-all duration-300 ease-out hover:scale-105 hover:shadow-md animate-in slide-in-from-bottom-2 duration-300 delay-300"
          >
            <RotateCcw className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            Повернутися
          </Button>
        )}
      </div>
    </div>
  );
};

export default UserSwitchPlaceholder;
